import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGridIcon, InboxIcon, MapPinIcon, BarChart3Icon, SparklesIcon, MegaphoneIcon, FileCheckIcon, TicketIcon, TruckIcon, CircleUserIcon, UsersIcon, SettingsIcon, CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { intelligenceIntro, sidebarItems } from '../../data/requestsIntelligence';
import { SectorHeatmap } from './intelligence/SectorHeatmap';
import { KpiRow, VolumeChart, PerformanceTable, SectorPanel, RecommendationPanel, DigitalReachBand } from './intelligence/DashboardPanels';
const EASE = [0.23, 1, 0.32, 1] as const;
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS: Record<string, typeof InboxIcon> = {
  dashboard: LayoutGridIcon,
  requests: InboxIcon,
  map: MapPinIcon,
  analytics: BarChart3Icon,
  atna: SparklesIcon,
  comms: MegaphoneIcon,
  permits: FileCheckIcon,
  leisure: TicketIcon,
  collect: TruckIcon,
  accounts: CircleUserIcon,
  users: UsersIcon,
  settings: SettingsIcon
};
export function RequestIntelligence() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: {
    fr: string;
    en: string;
  }) => isFR ? v.fr : v.en;
  return <div className="relative max-w-[1440px] mx-auto" data-id="element-6071" data-mp="5afd9zc0c8gb">
      <div className="pointer-events-none absolute left-[10%] top-[30%] w-[80%] h-[60%] rounded-full bg-[#EEF0FF] opacity-60 blur-[120px]" aria-hidden="true" data-id="element-6072" data-mp="5dbcdqzld36c" />

      {/* Intro */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative text-center max-w-[1080px] mx-auto" data-id="element-6073" data-mp="ebdgifemgbxu">
        <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-6074" data-mp="2c39ee6lilemm">{pick(intelligenceIntro.eyebrow)}</p>
        <h2 className="font-serif text-[2.4rem] sm:text-[3.2rem] xl:text-[3.9rem] text-[#121212] leading-[1.08] tracking-tight mt-5" data-id="element-6075" data-mp="f3ogrrdijdbb">
          {pick(intelligenceIntro.titleA)} <span className="text-[#4F46E5]" data-id="element-6076" data-mp="oxww1ddj99rj">{pick(intelligenceIntro.titleB)}</span>
        </h2>
        <p className="font-serif text-[1.35rem] sm:text-[1.65rem] text-[#6B6F99] mt-3" data-id="element-6077" data-mp="u05qke620jvr">{pick(intelligenceIntro.subline)}</p>
        <p className="font-sans text-[16px] md:text-[17px] text-[#4A5160] leading-[1.65] mt-5 max-w-[900px] mx-auto" data-id="element-6078" data-mp="xy3ezes031y3">
          {pick(intelligenceIntro.body)}
        </p>
      </motion.div>

      {/* Dashboard */}
      <motion.div initial={{
      opacity: 0,
      y: 18
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE,
      delay: 0.06
    }} className="relative mt-12 rounded-[22px] border border-[#E8E8F0] bg-[#FDFDFF] shadow-[0_30px_70px_-45px_rgba(40,36,120,0.35)] lg:grid lg:grid-cols-[196px_minmax(0,1fr)] overflow-hidden" data-id="element-6079" data-mp="1cp2lmk99zgf1">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col border-r border-[#EFEFF5] bg-white px-4 py-5" aria-label={isFR ? 'Navigation de l’application' : 'App navigation'} data-id="element-6080" data-mp="3254t6ww7iag6">
          <div className="flex items-center gap-2.5 px-2" data-id="element-6081" data-mp="2s89wmjjhuk4j">
            <img src={BCITI_LOGO} alt="BCITI" className="h-[18px] w-auto object-contain" data-id="element-6082" data-mp="2s9usqdlgib4i" />
            <span className="h-4 w-px bg-[#D5D7DF]" aria-hidden="true" data-id="element-6083" data-mp="3pv8z5awvamac" />
            <img src={ATNA_LOGO} alt="ATNA" className="h-[15px] w-auto object-contain" data-id="element-6084" data-mp="3kwflnihkyva6" />
          </div>
          <ul className="mt-8 space-y-1" data-id="element-6085" data-mp="3g4xj4pzwsmn3">
            {sidebarItems.map(item => {
            const Icon = NAV_ICONS[item.key];
            return <li key={item.key} aria-current={item.active ? 'page' : undefined} className={`flex items-center gap-3 rounded-lg px-3 py-2 font-sans text-[12.5px] ${item.active ? 'bg-[#F1F0FD] text-[#4F46E5] font-medium' : 'text-[#4A5160]'}`} data-id="element-6086" data-mp="cgvv4r8anqyt">
                  <Icon className={`w-4 h-4 ${item.active ? 'text-[#4F46E5]' : 'text-[#8A90A0]'}`} strokeWidth={1.7} aria-hidden="true" data-id="element-6087" data-mp="3sgou8fx7d4hp" />
                  {pick(item.label)}
                </li>;
          })}
          </ul>
        </aside>

        {/* Main */}
        <div className="p-4 md:p-5 space-y-3 min-w-0" data-id="element-6088" data-mp="aydpjh3noqb0">
          <header className="flex flex-wrap items-center justify-between gap-3 pb-1" data-id="element-6089" data-mp="b0zxhhugl7tr">
            <div className="flex items-center gap-3" data-id="element-6090" data-mp="fzta781x8usv">
              <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-6091" data-mp="19lz5de8ue27">
                <InboxIcon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-6092" data-mp="66cgs55p5hpq" />
              </span>
              <div data-id="element-6093" data-mp="tw6ueoxubab1">
                <p className="font-sans text-[17px] font-semibold text-[#121212]" data-id="element-6094" data-mp="tvomuw5q99nt">{isFR ? 'Analytique des requêtes' : 'Request analytics'}</p>
                <p className="font-sans text-[12px] text-[#6B7180]" data-id="element-6095" data-mp="2jdu0f2a7i7fb">
                  {isFR ? 'Vue d’ensemble et performance en temps réel.' : 'Real-time overview and performance.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-6096" data-mp="2s65poktl3otp">
              <span className="inline-flex items-center gap-2 rounded-lg border border-[#E6E7EE] bg-white px-3 py-2 font-sans text-[11.5px] text-[#3A4050]" data-id="element-6097" data-mp="2o53lrqoq2ole">
                <CalendarIcon className="w-3.5 h-3.5 text-[#6B7180]" aria-hidden="true" data-id="element-6098" data-mp="20hlz29kbegpa" />
                {isFR ? '30 derniers jours' : 'Last 30 days'}
                <ChevronDownIcon className="w-3.5 h-3.5 text-[#6B7180]" aria-hidden="true" data-id="element-6099" data-mp="29hifxp0gqb9s" />
              </span>
              <span className="hidden sm:inline-flex items-center gap-6 rounded-lg border border-[#E6E7EE] bg-white px-3 py-2 font-sans text-[11.5px] text-[#3A4050]" data-id="element-6100" data-mp="24ft5obl2e1mr">
                {isFR ? 'Tous les secteurs' : 'All sectors'}
                <ChevronDownIcon className="w-3.5 h-3.5 text-[#6B7180]" aria-hidden="true" data-id="element-6101" data-mp="3fyvvsdet3dw2" />
              </span>
              <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center font-sans text-[12px] font-semibold text-[#4F46E5]" data-id="element-6102" data-mp="2ii6im7uj4j5s">JD</span>
            </div>
          </header>

          <KpiRow isFR={isFR} data-id="element-6103" data-mp="2riwcv74561he" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)] gap-3" data-id="element-6104" data-mp="2rj4sv7vk9mwt">
            <div className="order-2 lg:order-1" data-id="element-6105" data-mp="1utf0686qrf67">
              <VolumeChart isFR={isFR} data-id="element-6106" data-mp="1uqj28juw8qhr" />
            </div>
            <div className="order-1 lg:order-2" data-id="element-6107" data-mp="1yv8ve0y63qyz">
              <PerformanceTable isFR={isFR} data-id="element-6108" data-mp="1yrus8qhojdqh" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.85fr)_minmax(0,0.95fr)] gap-3" data-id="element-6109" data-mp="23rzd0spmeumi">
            <div className="md:col-span-2 xl:col-span-1" data-id="element-6110" data-mp="1b4ms0mz3yuvb">
              <SectorHeatmap isFR={isFR} data-id="element-6111" data-mp="1b43f5kobewo5" />
            </div>
            <SectorPanel isFR={isFR} data-id="element-6112" data-mp="1g2xycnq72n8s" />
            <RecommendationPanel isFR={isFR} data-id="element-6113" data-mp="1g1wmih1ctwq0" />
          </div>

          <DigitalReachBand isFR={isFR} data-id="element-6114" data-mp="18kgmwbyrcyd6" />
        </div>
      </motion.div>
    </div>;
}